import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = '/api/bitlocker';
function auth() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } };
}

const PROTECTION_META = {
  on:        { color: '#16a34a', bg: '#dcfce7', label: '🔒 Encrypted',     badge: 'green' },
  off:       { color: '#dc2626', bg: '#fee2e2', label: '🔓 Not Encrypted',  badge: 'red'  },
  suspended: { color: '#d97706', bg: '#fef3c7', label: '⏸ Suspended',      badge: 'amber'},
  unknown:   { color: '#94a3b8', bg: '#f1f5f9', label: '⏳ Awaiting Agent', badge: 'grey' },
};

function timeSince(d) {
  if (!d) return '—';
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 1)    return 'Just now';
  if (m < 60)   return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
}

function EncBar({ pct, status }) {
  if (status === 'unknown' || pct === null || pct === undefined) {
    return <span style={{ fontSize: 11, color: '#94a3b8' }}>Awaiting data</span>;
  }
  const color = status === 'on' ? '#16a34a' : status === 'suspended' ? '#d97706' : '#e2e8f0';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 8, background: '#e8ecf4', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct || 0}%`, background: color, borderRadius: 4, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: 11, color: '#64748b', minWidth: 32, textAlign: 'right' }}>{pct}%</span>
    </div>
  );
}

export default function BitLockerStatus({ user }) {
  const isAdmin = user?.role === 'admin';

  const [summary,     setSummary]     = useState(null);
  const [devices,     setDevices]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [msg,         setMsg]         = useState({ text: '', type: '' });
  const [search,      setSearch]      = useState('');
  const [filterStatus,setFilterStatus]= useState('all');
  const [selected,    setSelected]    = useState([]);
  const [enforcing,   setEnforcing]   = useState(false);
  const [decrypting,  setDecrypting]  = useState(false);
  const [showDecryptModal, setShowDecryptModal] = useState(false);
  const [decryptReason,    setDecryptReason]    = useState('');
  const [detailDev,   setDetailDev]   = useState(null);   // device being inspected
  const [revealKeys,  setRevealKeys]  = useState({});     // volumeId → plain key
  const [revealing,   setRevealing]   = useState({});     // volumeId → bool

  function flash(text, type = 'success') {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 5000);
  }

  const loadAll = useCallback(async () => {
    setLoading(true);
    // Check token exists first
    const token = localStorage.getItem('adminToken');
    if (!token) {
      flash('Session expired — please log in again', 'error');
      setLoading(false);
      return;
    }
    try {
      const [sumRes, devRes] = await Promise.all([
        axios.get(`${API}/summary`, auth()),
        axios.get(API,             auth()),
      ]);
      setSummary(sumRes.data);
      setDevices(devRes.data.devices || []);
      // Clear any previous error on success
      setMsg({ text: '', type: '' });
    } catch (e) {
      const status = e.response?.status;
      if (status === 401) {
        // Session expired — clear token and force re-login
        localStorage.removeItem('adminToken');
        localStorage.removeItem('currentUser');
        window.location.reload();
        return;
      }
      flash('Failed to load BitLocker data: ' + (e.response?.data?.error || e.message), 'error');
      // Still try to load devices separately if summary failed
      try {
        const devRes = await axios.get(API, auth());
        setDevices(devRes.data.devices || []);
      } catch (_) {}
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh every 2 minutes — data appears when agents report in
  useEffect(() => {
    const interval = setInterval(loadAll, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadAll]);

  // ── Enforce encryption on selected devices ──────────────────────────────
  async function enforceEncryption() {
    if (!selected.length) return flash('Select at least one device', 'error');
    setEnforcing(true);
    try {
      const res = await axios.post(`${API}/enforce`, { deviceIds: selected }, auth());
      flash(`✔ ${res.data.message}`);
      setSelected([]);
      loadAll();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    } finally {
      setEnforcing(false);
    }
  }

  // ── Decrypt (disable BitLocker) on selected devices ─────────────────────
  async function decryptDevices() {
    if (!selected.length || !decryptReason.trim()) return;
    setDecrypting(true);
    try {
      const res = await axios.post(`${API}/decrypt`, {
        deviceIds: selected,
        reason: decryptReason.trim(),
      }, auth());
      flash(`✔ ${res.data.message}`);
      setSelected([]);
      setShowDecryptModal(false);
      setDecryptReason('');
      loadAll();
    } catch (e) {
      flash(e.response?.data?.error || e.message, 'error');
    } finally {
      setDecrypting(false);
    }
  }

  // ── Reveal recovery key for a volume ───────────────────────────────────
  async function revealKey(deviceId, mountPoint, volumeId) {
    setRevealing(prev => ({ ...prev, [volumeId]: true }));
    try {
      const res = await axios.get(`${API}/${deviceId}/recovery-key?mountPoint=${encodeURIComponent(mountPoint)}`, auth());
      setRevealKeys(prev => ({ ...prev, [volumeId]: res.data.recoveryKey }));
    } catch (e) {
      flash('Could not retrieve recovery key: ' + (e.response?.data?.error || e.message), 'error');
    } finally {
      setRevealing(prev => ({ ...prev, [volumeId]: false }));
    }
  }

  function hideKey(volumeId) {
    setRevealKeys(prev => { const n = { ...prev }; delete n[volumeId]; return n; });
  }

  // ── Load detail for one device ─────────────────────────────────────────
  async function openDetail(deviceId) {
    try {
      const res = await axios.get(`${API}/${deviceId}`, auth());
      setDetailDev(res.data);
    } catch (e) {
      flash('Could not load device detail: ' + (e.response?.data?.error || e.message), 'error');
    }
  }

  // ── Filtered list ──────────────────────────────────────────────────────
  const filtered = devices.filter(d => {
    const matchStatus = filterStatus === 'all' || d.protectionStatus === filterStatus;
    const matchSearch = !search || d.hostname?.toLowerCase().includes(search.toLowerCase())
                                || d.ipAddress?.includes(search);
    return matchStatus && matchSearch;
  });

  function toggleSelect(id) {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }
  function selectAll() {
    const ids = filtered.filter(d => d.protectionStatus !== 'on').map(d => d.deviceId);
    setSelected(ids);
  }
  function clearSelect() { setSelected([]); }

  const unencryptedCount = devices.filter(d => d.protectionStatus === 'off').length;
  const unknownCount     = devices.filter(d => d.protectionStatus === 'unknown').length;
  const onlineWithData   = devices.filter(d => d.status === 'online' && d.protectionStatus !== 'unknown').length;
  const onlineNoData     = devices.filter(d => d.status === 'online' && d.protectionStatus === 'unknown').length;
  const offlineNoData    = devices.filter(d => d.status !== 'online' && d.protectionStatus === 'unknown').length;

  return (
    <div>
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <div className="page-title">🔐 BitLocker Encryption</div>
          <div className="page-subtitle">
            Drive encryption status · Recovery key vault · Admin-enforced encryption
          </div>
        </div>
        <div className="flex-gap">
          {isAdmin && selected.length > 0 && (
            <>
              <button className="btn btn-primary" onClick={enforceEncryption} disabled={enforcing}>
                {enforcing ? '⟳ Enforcing...' : `🔒 Enforce on ${selected.length} Device(s)`}
              </button>
              <button
                className="btn btn-ghost"
                style={{ color: '#dc2626', border: '1px solid #fca5a5' }}
                onClick={() => setShowDecryptModal(true)}
                disabled={decrypting}
              >
                🔓 Decrypt {selected.length} Device(s)
              </button>
            </>
          )}
          <button className="refresh-btn" onClick={loadAll} disabled={loading}>↻ Refresh</button>
        </div>
      </div>

      {/* ── Alert ───────────────────────────────────────────────────── */}
      {msg.text && (
        <div className={`alert ${msg.type === 'error' ? 'error' : 'success'}`} style={{ marginBottom: 16 }}>
          {msg.text}
        </div>
      )}

      {/* ── Info banners ─────────────────────────────────────────────── */}
      {/* Online devices awaiting first scan */}
      {!loading && onlineNoData > 0 && (
        <div style={{
          background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10,
          padding: '12px 18px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 18 }}>⏳</span>
          <div style={{ fontSize: 13 }}>
            <strong style={{ color: '#0369a1' }}>{onlineNoData} online device(s)</strong>
            <span style={{ color: '#0284c7' }}> are awaiting first BitLocker scan. Data will appear within 10 minutes.</span>
          </div>
        </div>
      )}
      {/* Offline devices */}
      {!loading && offlineNoData > 0 && (
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
          padding: '12px 18px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 18 }}>📴</span>
          <div style={{ fontSize: 13 }}>
            <strong style={{ color: '#64748b' }}>{offlineNoData} offline device(s)</strong>
            <span style={{ color: '#94a3b8' }}> will report BitLocker status when they reconnect to the network.</span>
          </div>
        </div>
      )}

      {/* ── Unencrypted warning banner ───────────────────────────────── */}
      {!loading && unencryptedCount > 0 && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
          padding: '12px 18px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div>
            <strong style={{ color: '#dc2626' }}>{unencryptedCount} device(s) are not encrypted.</strong>
            <span style={{ color: '#ef4444', marginLeft: 8, fontSize: 13 }}>
              Select devices below and click "Enforce Encryption" to remediate.
            </span>
          </div>
        </div>
      )}

      {/* ── KPI cards ───────────────────────────────────────────────── */}
      {summary && (
        <div className="cards-row" style={{ gridTemplateColumns: 'repeat(6,1fr)', marginBottom: 20 }}>
          {[
            { label: 'Total Devices',   value: summary.totalDevices,      cls: 'blue'  },
            { label: 'Encrypted',        value: summary.encrypted,         cls: 'green' },
            { label: 'Not Encrypted',    value: summary.notEncrypted,      cls: 'red',  alert: summary.notEncrypted > 0 },
            { label: 'Suspended',        value: summary.suspended,         cls: 'amber' },
            { label: 'Unknown',          value: summary.unknown,           cls: 'grey'  },
            { label: 'Compliance %',     value: `${summary.compliancePercent}%`,
              cls: summary.compliancePercent >= 90 ? 'green' : summary.compliancePercent >= 70 ? 'amber' : 'red' },
          ].map(c => (
            <div key={c.label}
              className={`card ${c.cls}`}
              style={{ outline: c.alert ? '2px solid #dc2626' : undefined }}>
              <div className="card-label">{c.label}</div>
              <div className="card-value">{c.value ?? '—'}</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Filter + search bar ─────────────────────────────────────── */}
      <div className="flex-gap" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          className="form-input" style={{ maxWidth: 240 }}
          placeholder="Search hostname or IP..."
          value={search} onChange={e => setSearch(e.target.value)}
        />
        <div className="flex-gap">
          {['all', 'on', 'off', 'suspended', 'unknown'].map(s => {
            const m = PROTECTION_META[s] || PROTECTION_META.unknown;
            return (
              <button key={s}
                onClick={() => setFilterStatus(s)}
                style={{
                  padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  border: 'none', cursor: 'pointer',
                  background: filterStatus === s ? (m.color || '#1a56db') : (m.bg || '#f1f5f9'),
                  color: filterStatus === s ? '#fff' : (m.color || '#64748b'),
                }}>
                {s === 'all' ? `All (${devices.length})` : `${m.label} (${devices.filter(d => d.protectionStatus === s).length})`}
              </button>
            );
          })}
        </div>
        {isAdmin && filtered.length > 0 && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-sm btn-ghost" onClick={selectAll}>
              ☑ Select Unencrypted
            </button>
            <button className="btn btn-sm btn-ghost" style={{ color: '#dc2626' }}
              onClick={() => {
                const ids = filtered.filter(d => d.protectionStatus === 'on').map(d => d.deviceId);
                setSelected(ids);
              }}>
              ☑ Select Encrypted
            </button>
            {selected.length > 0 && (
              <button className="btn btn-sm btn-ghost" onClick={clearSelect}>
                ✕ Clear ({selected.length})
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Device table ────────────────────────────────────────────── */}
      <div className="panel">
        <div className="panel-header">
          <div className="panel-title">
            <div className="panel-title-icon">🔐</div>
            Endpoint Encryption Status
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8' }}>{filtered.length} device(s)</div>
        </div>

        {loading ? (
          <div className="loading">Loading BitLocker data...</div>
        ) : filtered.length === 0 ? (
          <div className="empty" style={{ padding: 48 }}>
            {devices.length === 0
              ? 'No devices have reported BitLocker status yet. Agents will report on next heartbeat.'
              : filterStatus === 'unknown'
              ? `${unknownCount} device(s) awaiting data — ${onlineNoData} online (collecting soon), ${offlineNoData} offline (will report when reconnected).`
              : 'No devices match the current filter.'}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                {isAdmin && <th style={{ width: 36 }}></th>}
                <th>Hostname</th>
                <th>IP Address</th>
                <th>Status</th>
                <th>Encryption</th>
                <th>Volume Status</th>
                <th>Last Checked</th>
                <th>Recovery Key</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => {
                const pm    = PROTECTION_META[d.protectionStatus] || PROTECTION_META.unknown;
                const isSel = selected.includes(d.deviceId);
                // Use a stable key-like ID for the primary volume
                const primaryVolId = d.deviceId + ':' + (d.mountPoint || 'C:');

                return (
                  <tr key={d.deviceId}
                    style={{ background: isSel ? '#eff4ff' : undefined, cursor: 'pointer' }}
                    onClick={() => isAdmin && toggleSelect(d.deviceId)}>

                    {isAdmin && (
                      <td onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={isSel}
                          onChange={() => toggleSelect(d.deviceId)} />
                      </td>
                    )}

                    <td>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{d.hostname}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>{d.os || '—'}</div>
                    </td>

                    <td style={{ fontSize: 12, color: '#64748b' }}>{d.ipAddress || '—'}</td>

                    <td>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                        background: pm.bg, color: pm.color,
                      }}>
                        {pm.label}
                      </span>
                      {d.enforced && (
                        <div style={{ fontSize: 10, color: '#7c3aed', marginTop: 3 }}>
                          ⚡ Enforced by admin
                        </div>
                      )}
                    </td>

                    <td style={{ minWidth: 140 }}>
                      <EncBar pct={d.encryptionPercentage} status={d.protectionStatus} />
                    </td>

                    <td style={{ fontSize: 11, color: '#64748b' }}>
                      {d.volumeStatus || '—'}
                    </td>

                    <td style={{ fontSize: 11, color: '#94a3b8' }}>
                      {timeSince(d.lastChecked)}
                    </td>

                    <td onClick={e => e.stopPropagation()}>
                      {d.hasRecoveryKey ? (
                        isAdmin ? (
                          revealKeys[primaryVolId] ? (
                            <div>
                              <code style={{
                                fontSize: 10, background: '#f1f5f9', padding: '3px 6px',
                                borderRadius: 4, userSelect: 'all', display: 'block',
                                maxWidth: 200, wordBreak: 'break-all',
                              }}>
                                {revealKeys[primaryVolId]}
                              </code>
                              <button className="btn btn-sm btn-ghost"
                                style={{ fontSize: 10, marginTop: 4 }}
                                onClick={() => hideKey(primaryVolId)}>
                                🙈 Hide
                              </button>
                            </div>
                          ) : (
                            <button className="btn btn-sm btn-ghost"
                              disabled={revealing[primaryVolId]}
                              onClick={() => revealKey(d.deviceId, d.mountPoint || 'C:', primaryVolId)}>
                              {revealing[primaryVolId] ? '⟳' : '👁 Reveal Key'}
                            </button>
                          )
                        ) : (
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>🔒 Admin only</span>
                        )
                      ) : (
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>
                      )}
                    </td>

                    <td onClick={e => e.stopPropagation()}>
                      <button className="btn btn-sm btn-ghost"
                        onClick={() => openDetail(d.deviceId)}>
                        Details
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Device Detail Modal ─────────────────────────────────────── */}
      {detailDev && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: 28, width: 640,
            maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.22)',
          }}>
            {/* Modal header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#1e2a3b' }}>
                  🔐 {detailDev.hostname}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  {detailDev.os} · {detailDev.ipAddress}
                </div>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setDetailDev(null)}>✕ Close</button>
            </div>

            {/* Volume list */}
            {detailDev.volumes?.length === 0 ? (
              <div className="empty" style={{ padding: 32 }}>No BitLocker volume data available.</div>
            ) : (
              detailDev.volumes?.map(vol => {
                const pm     = PROTECTION_META[vol.protectionStatus] || PROTECTION_META.unknown;
                const volKey = detailDev.deviceId + ':' + vol.mountPoint;
                return (
                  <div key={vol.id || vol.mountPoint} style={{
                    border: '1px solid #e8ecf4', borderRadius: 10, padding: 18, marginBottom: 14,
                  }}>
                    {/* Volume header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                      <div style={{
                        fontSize: 24, width: 44, height: 44, background: pm.bg,
                        borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {vol.protectionStatus === 'on' ? '🔒' : '🔓'}
                      </div>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15 }}>{vol.mountPoint}</div>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '2px 10px',
                          borderRadius: 20, background: pm.bg, color: pm.color,
                        }}>
                          {pm.label}
                        </span>
                      </div>
                      <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>Last checked</div>
                        <div style={{ fontSize: 12 }}>{timeSince(vol.lastChecked)}</div>
                      </div>
                    </div>

                    {/* Stats grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
                      <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>ENCRYPTION PROGRESS</div>
                        <EncBar pct={vol.encryptionPercentage} status={vol.protectionStatus} />
                      </div>
                      <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>VOLUME STATUS</div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{vol.volumeStatus || '—'}</div>
                      </div>
                      <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>ENCRYPTION METHOD</div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{vol.encryptionMethod || '—'}</div>
                      </div>
                      <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px' }}>
                        <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4 }}>KEY PROTECTOR ID</div>
                        <div style={{ fontSize: 11, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {vol.keyProtectorId ? vol.keyProtectorId.slice(0, 20) + '…' : '—'}
                        </div>
                      </div>
                    </div>

                    {/* Key Protectors — shows ALL types including TPM */}
                    {vol.keyProtectors && (() => {
                      let kps = [];
                      try { kps = JSON.parse(vol.keyProtectors); } catch (_) {}
                      if (!kps.length) return null;
                      const TYPE_META = {
                        Tpm:             { icon: '🔐', color: '#1d4ed8', bg: '#dbeafe', label: 'TPM (hardware-bound, not exportable)' },
                        TpmPin:          { icon: '🔐', color: '#7c3aed', bg: '#ede9fe', label: 'TPM + PIN' },
                        TpmNetworkKey:   { icon: '🔐', color: '#0891b2', bg: '#cffafe', label: 'TPM + Network Key' },
                        RecoveryPassword:{ icon: '🔑', color: '#92400e', bg: '#fef3c7', label: 'Recovery Password (48-digit)' },
                        ExternalKey:     { icon: '💾', color: '#64748b', bg: '#f1f5f9', label: 'External Key (USB)' },
                        Password:        { icon: '🔒', color: '#64748b', bg: '#f1f5f9', label: 'Password' },
                      };
                      return (
                        <div style={{ marginTop: 12 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>
                            Key Protectors ({kps.length})
                          </div>
                          {kps.map((kp, i) => {
                            const m = TYPE_META[kp.type] || { icon: '🔑', color: '#64748b', bg: '#f1f5f9', label: kp.type };
                            return (
                              <div key={i} style={{
                                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                                background: m.bg, borderRadius: 6, marginBottom: 4,
                              }}>
                                <span style={{ fontSize: 14 }}>{m.icon}</span>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 12, fontWeight: 600, color: m.color }}>{kp.type}</div>
                                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{m.label}</div>
                                </div>
                                <code style={{ fontSize: 10, color: '#64748b', fontFamily: 'monospace' }}>
                                  {kp.id ? kp.id.slice(1, 9) + '…' : '—'}
                                </code>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}

                    {/* Recovery key section — admin only */}
                    {vol.hasRecoveryKey && (
                      <div style={{
                        background: '#fffbeb', border: '1px solid #fde68a',
                        borderRadius: 8, padding: '12px 16px', marginTop: 10,
                      }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', marginBottom: 8 }}>
                          🔑 Recovery Password (48-digit)
                        </div>
                        {!isAdmin ? (
                          <div style={{ fontSize: 12, color: '#92400e' }}>🔒 Admin access required.</div>
                        ) : revealKeys[volKey] ? (
                          <div>
                            <code style={{
                              fontSize: 13, background: '#fff', border: '1px solid #fde68a',
                              padding: '8px 12px', borderRadius: 6, display: 'block',
                              userSelect: 'all', letterSpacing: '2px', wordBreak: 'break-all',
                            }}>
                              {revealKeys[volKey]}
                            </code>
                            <div style={{ fontSize: 11, color: '#92400e', marginTop: 6 }}>
                              ⚠ Access recorded in audit log.
                            </div>
                            <button className="btn btn-sm btn-ghost" style={{ marginTop: 8, fontSize: 11 }}
                              onClick={() => hideKey(volKey)}>
                              🙈 Hide Key
                            </button>
                          </div>
                        ) : (
                          <button className="btn btn-primary btn-sm"
                            disabled={revealing[volKey]}
                            onClick={() => revealKey(detailDev.deviceId, vol.mountPoint, volKey)}>
                            {revealing[volKey] ? '⟳ Loading...' : '👁 Reveal Recovery Key'}
                          </button>
                        )}
                      </div>
                    )}

                    {!vol.hasRecoveryKey && vol.protectionStatus === 'on' && (
                      <div style={{
                        background: '#f0f9ff', border: '1px solid #bae6fd',
                        borderRadius: 8, padding: '10px 14px', marginTop: 10,
                      }}>
                        <div style={{ fontSize: 12, color: '#0369a1' }}>
                          ℹ No recovery password stored. Agent will auto-add one on next scan (within 30 min).
                        </div>
                      </div>
                    )}
                    {!vol.hasRecoveryKey && vol.protectionStatus !== 'on' && (
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>
                        Volume is not encrypted — no recovery key needed.
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {/* Enforce button in modal */}
            {isAdmin && detailDev.protectionStatus !== 'on' && (
              <div style={{ marginTop: 8, borderTop: '1px solid #e8ecf4', paddingTop: 16 }}>
                <button className="btn btn-primary"
                  disabled={enforcing}
                  onClick={async () => {
                    setEnforcing(true);
                    try {
                      const res = await axios.post(`${API}/enforce`,
                        { deviceIds: [detailDev.deviceId] }, auth());
                      flash(`✔ ${res.data.message}`);
                      setDetailDev(null);
                      loadAll();
                    } catch (e) {
                      flash(e.response?.data?.error || e.message, 'error');
                    } finally { setEnforcing(false); }
                  }}>
                  {enforcing ? '⟳ Enforcing...' : '🔒 Enforce BitLocker on This Device'}
                </button>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                  Agent will enable BitLocker on next heartbeat (within 10 minutes).
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Decrypt Confirm Modal ────────────────────────────────────── */}
      {showDecryptModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 14, padding: 28, width: 500,
            boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 10, background: '#fee2e2',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
              }}>🔓</div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#dc2626' }}>
                  Disable BitLocker Encryption
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  {selected.length} device(s) selected
                </div>
              </div>
            </div>

            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
              padding: '12px 16px', marginBottom: 18,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#dc2626', marginBottom: 4 }}>
                ⚠ Security Warning
              </div>
              <div style={{ fontSize: 12, color: '#7f1d1d', lineHeight: 1.6 }}>
                Disabling BitLocker will <strong>remove drive encryption</strong> from the selected device(s).
                This action is permanently logged. The agent will run <code>manage-bde -off C:</code> on the device.
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>
                Devices to decrypt:
              </div>
              <div style={{ border: '1px solid #e8ecf4', borderRadius: 8, maxHeight: 120, overflowY: 'auto' }}>
                {devices.filter(d => selected.includes(d.deviceId)).map(d => {
                  const pm = PROTECTION_META[d.protectionStatus] || PROTECTION_META.unknown;
                  return (
                    <div key={d.deviceId} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '8px 12px', borderBottom: '1px solid #f0f4ff',
                    }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{d.hostname}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{d.ipAddress}</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px',
                        borderRadius: 20, background: pm.bg, color: pm.color }}>
                        {pm.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 20 }}>
              <label className="form-label" style={{ color: '#dc2626' }}>
                Reason * (required for audit trail)
              </label>
              <textarea className="form-input" rows={3}
                placeholder="e.g. Device decommissioned, hardware replacement, IT request #12345"
                value={decryptReason} onChange={e => setDecryptReason(e.target.value)}
                style={{ resize: 'vertical' }} />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                Minimum 5 characters · Recorded permanently in audit log
              </div>
            </div>

            <div className="flex-gap" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost"
                onClick={() => { setShowDecryptModal(false); setDecryptReason(''); }}>
                Cancel
              </button>
              <button className="btn btn-primary"
                style={{ background: '#dc2626', borderColor: '#dc2626' }}
                onClick={decryptDevices}
                disabled={decrypting || decryptReason.trim().length < 5}>
                {decrypting ? '⟳ Queuing...' : `🔓 Confirm Decrypt ${selected.length} Device(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
